/**
 * Public AppSync GraphQL (API key) — used when generateClient has no model introspection
 * for a model that still exists on the API (e.g. TherapistProfile).
 */

export type AmplifyOutputsDataConfig = { url?: string; api_key?: string };

let amplifyOutputsDataConfigCache: AmplifyOutputsDataConfig | null = null;

export async function getAmplifyDataApiConfig(): Promise<AmplifyOutputsDataConfig> {
  if (amplifyOutputsDataConfigCache) return amplifyOutputsDataConfigCache;
  const mod = await import('../../amplify_outputs.json');
  const outputs = (mod as { default?: unknown }).default ?? mod;
  const cfg: AmplifyOutputsDataConfig = (outputs as { data?: AmplifyOutputsDataConfig })?.data ?? {};
  amplifyOutputsDataConfigCache = cfg;
  return cfg;
}

export function stripUndefined<T extends Record<string, unknown>>(obj: T): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

export async function appsyncPublicGraphqlRequest<T = unknown>(
  query: string,
  variables?: Record<string, unknown>,
): Promise<T> {
  const cfg = await getAmplifyDataApiConfig();
  if (!cfg.url || !cfg.api_key) {
    throw new Error('Amplify data API is not configured (missing data.url or data.api_key in amplify_outputs.json).');
  }

  const res = await fetch(cfg.url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': cfg.api_key,
    },
    body: JSON.stringify({ query, variables }),
  });
  const text = await res.text();
  let json: { data?: T; errors?: Array<{ message?: string }> };
  try {
    json = JSON.parse(text) as { data?: T; errors?: Array<{ message?: string }> };
  } catch {
    throw new Error(
      res.ok
        ? 'Invalid response from API (not JSON).'
        : `API request failed (${res.status}). ${text.slice(0, 200)}`,
    );
  }
  if (!res.ok) {
    const msg = json.errors?.map((e) => e.message).join('; ') || text.slice(0, 300);
    throw new Error(`API request failed (${res.status}): ${msg}`);
  }
  if (json.errors && json.errors.length > 0) {
    throw new Error(json.errors.map((e) => e.message || 'GraphQL error').join('; '));
  }
  return json.data as T;
}
